import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';

import { Button, Dialog, TextField } from '@zendesk/forethought-elements';
import { useUpdateOrchestratorWorkflowMutation } from 'src/services/orchestrator/orchestratorApi';
import { setGlobalToastOptions } from 'src/slices/ui/uiSlice';
import { useAppDispatch } from 'src/store/hooks';

interface EditAutomationModalProps {
  initialDescription: string | null;
  initialName: string;
  isOpen: boolean;
  onClose: () => void;
  workflowId: string;
}

export const EditAutomationModal = ({
  initialDescription,
  initialName,
  isOpen,
  onClose,
  workflowId,
}: EditAutomationModalProps) => {
  const { t } = useTranslation('orchestrator');
  const dispatch = useAppDispatch();
  const [updateWorkflow, { isLoading }] =
    useUpdateOrchestratorWorkflowMutation();

  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription || '');

  useEffect(() => {
    if (isOpen) {
      setName(initialName);
      setDescription(initialDescription || '');
    }
  }, [isOpen, initialName, initialDescription]);

  const handleClose = () => {
    onClose();
  };

  const handleSave = async () => {
    try {
      await updateWorkflow({
        request: {
          workflow_description: description || null,
          workflow_name: name,
        },
        workflowId,
      }).unwrap();

      dispatch(
        setGlobalToastOptions({
          title: t('editAutomationModal.toastUpdateSuccess'),
          variant: 'main',
        }),
      );
      onClose();
    } catch {
      dispatch(
        setGlobalToastOptions({
          title: t('editAutomationModal.toastUpdateError'),
          variant: 'danger',
        }),
      );
    }
  };

  const isValid = name.trim().length > 0;

  return (
    <Dialog
      footer={
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            width: '100%',
          }}
        >
          <Button onClick={handleClose} variant='ghost'>
            {t('addJsonDrawer.cancel')}
          </Button>
          <Button
            disabled={!isValid}
            fullWidth
            isLoading={isLoading}
            onClick={handleSave}
            variant='main'
          >
            {t('addJsonDrawer.save')}
          </Button>
        </Box>
      }
      hideCloseButton
      onClose={handleClose}
      open={isOpen}
      title={t('editAutomationModal.title')}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          width: '548px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Box
            component='label'
            sx={{
              fontSize: '14px',
              fontWeight: 600,
            }}
          >
            {t('createAutomationModal.nameLabel')}
          </Box>
          <TextField
            aria-label={t('createAutomationModal.nameAriaLabel')}
            onChange={e => setName(e.target.value)}
            placeholder={t('createAutomationModal.namePlaceholder')}
            value={name}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Box
            component='label'
            sx={{
              fontSize: '14px',
              fontWeight: 600,
            }}
          >
            {t('createAutomationModal.descriptionLabel')}
          </Box>
          <TextField
            aria-label={t('createAutomationModal.descriptionLabel')}
            multiline
            onChange={e => setDescription(e.target.value)}
            placeholder={t('createAutomationModal.descriptionPlaceholder')}
            rows={3}
            value={description}
          />
        </Box>
      </Box>
    </Dialog>
  );
};
